import { TeamReviewMemberOrder } from "models/teams/teamReview/TeamReviewMemberOrder";
import { TeamReviewSettings } from "models/teams/teamReview/TeamReviewSettings";
import { TeamReviewApprovalSetting } from "models/teams/teamReview/TeamReviewApprovalSetting";
import { TeamReviewMemberOrderSetting } from "models/teams/teamReview/TeamReviewMemberOrderSetting";
import { buildDefaultTeamReviewSettingsDto, fromTeamReviewSettingsDto } from "models/teams/teamReview/TeamReviewSettingsDto";
import React, { useEffect } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ApiService } from "services/ApiService";
import { Team, Teammate } from "models/teams/Team";

export interface TeamReviewSettingsProps {
    teamId: string;
    memberNames: Teammate[];
}

const TeamReviewSettingsComponent: React.FC<TeamReviewSettingsProps> = ({ teamId, memberNames }) => {

    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [teamReviewMemberOrder, setTeamReviewMemberOrder] = React.useState<TeamReviewMemberOrder[]>([]);
    const [teamReviewMemberOrderSetting, setTeamReviewMemberOrderSetting] = React.useState<TeamReviewMemberOrderSetting>(TeamReviewMemberOrderSetting.Any);
    const [teamReviewApprovalSetting, setTeamReviewApprovalSetting] = React.useState<TeamReviewApprovalSetting>(TeamReviewApprovalSetting.AllPass);
    const [teamReviewSettings, setTeamReviewSettings] = React.useState<TeamReviewSettings>(new TeamReviewSettings());
    const [settingsChanged, setSettingsChanged] = React.useState<boolean>(false);
    const [team, setTeam] = React.useState<Team | null>(null);
    const [isNewSettings, setIsNewSettings] = React.useState<boolean>(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Fetch team first to build defaults if needed
                const teamResponse = await ApiService.getTeamAsync(teamId, '');
                const currentTeam = teamResponse.data;
                if (currentTeam) {
                    setTeam(currentTeam);
                }

                // Attempt to fetch existing settings
                const response = await ApiService.getTeamReviewSettings(teamId, '');
                const settings = Array.isArray(response.data) ? response.data[0] : response.data;

                if (settings && settings.teamId) {
                    setTeamReviewSettings(settings);
                    setTeamReviewMemberOrderSetting((settings as any).teamReviewMemberOrder ?? settings.teamReviewMemberOrderSetting);
                    const existingOrder = (settings as any).teamOrder ?? settings.teamMemberOrder ?? [];
                    setTeamReviewMemberOrder(existingOrder);
                    setIsNewSettings(false);
                } else {
                    const sourceMembers: Teammate[] = currentTeam?.teammates && currentTeam.teammates.length > 0 ? currentTeam.teammates : (memberNames || []);
                    const defaultDto = buildDefaultTeamReviewSettingsDto(teamId, sourceMembers.map(m => ({ id: m.id, firstName: m.firstName, lastName: m.lastName })));
                    const defaultModel = fromTeamReviewSettingsDto(defaultDto);
                    // retain teamOrder for UI compatibility
                    (defaultModel as any).teamOrder = defaultDto.teamOrder;
                    setTeamReviewSettings(defaultModel);
                    setTeamReviewMemberOrderSetting(defaultModel.teamReviewMemberOrderSetting);
                    setTeamReviewMemberOrder(defaultModel.teamMemberOrder);
                    setIsNewSettings(true);
                    setSettingsChanged(true);
                }

                setLoading(false);
            } catch (error) {
                setError("Failed to fetch team data.");
                console.error("Fetch error:", error);
            }
        };
        fetchSettings();
    }, [teamId]);

    const sensors = useSensors(useSensor(PointerSensor));
    const handleTeamReviewSettingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSettingsChanged(() => true);
        if (e.target.id === "team-review-member-order-setting") {
            setTeamReviewMemberOrderSetting(Number(e.target.value) as TeamReviewMemberOrderSetting);
        }
        else if (e.target.id === "review-setting") {
            setTeamReviewApprovalSetting(Number(e.target.value) as TeamReviewApprovalSetting);
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = teamReviewMemberOrder.findIndex((item) => item.teamMemberId === active.id);
            const newIndex = teamReviewMemberOrder.findIndex((item) => item.teamMemberId === over.id);
            const newOrder = arrayMove(teamReviewMemberOrder, oldIndex, newIndex);

            newOrder.forEach((item, idx) => {
                item.position = idx + 1;
            });

            setTeamReviewMemberOrder([...newOrder]);
            setSettingsChanged(() => true);
        }
    };

    if (error) {
        return <div className="text-red-500">{error}</div>;
    }

    if (loading) {
        return <h2>Loading...</h2>;
    }

    const handleSaveSettings = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();

        const updatedSettings: TeamReviewSettings = {
            ...teamReviewSettings,
            teamReviewMemberOrderSetting: teamReviewMemberOrderSetting,
            teamReviewApprovalSetting: teamReviewApprovalSetting,
            teamMemberOrder: teamReviewMemberOrder,
        };

        try {
            if (isNewSettings) {
                const created = await ApiService.createTeamReviewSettings(updatedSettings);
                if (created.data) {
                    setTeamReviewSettings(created.data);
                    setIsNewSettings(false);
                }
            } else {
                await ApiService.updateTeamReviewSettings(updatedSettings);
            }
            setSettingsChanged(false);
        } catch (error) {
            setError("Failed to save settings.");
            console.error("Save error:", error);
        }
    };

    return (
        <>
            <div>
                <h2 className="text-xl font-bold mb-4">Team Review Settings</h2>
                <p>Configure the review settings for your team members. This allows you to customize how reviews are conducted within your team.</p>
                <p>A review is a process in which team members evaluate a client's submission. Whether it's a form submission or another type of content, having clear review settings helps streamline the process</p>
            </div>
            <div>
                <label htmlFor="team-review-setting-id" className="block mb-2 font-semibold">
                    Review Setting
                </label>
                <p>Select how you'd like a submission to go through the review process.</p>
                <p>Any Pass: Any team member can approve the submission. It only takes one approval</p>
                <p>All Pass: All team members must approve the submission for it to be accepted.</p>
                <select
                    id="review-setting"
                    value={teamReviewApprovalSetting}
                    onChange={handleTeamReviewSettingChange}
                    className="border rounded px-3 py-2"
                >
                    <option value={TeamReviewApprovalSetting.AllPass}>All Pass</option>
                    <option value={TeamReviewApprovalSetting.AnyPass}>Any Pass</option>
                </select>
            </div>
            <div className="mt-4">
                <label htmlFor="team-review-member-order-setting-id" className="block mb-2 font-semibold">
                    Team Member Review Order Settings
                </label>
                <p>If you want to change the order in which team members review submissions, you can adjust the settings here.</p>
                <p>Any: Team members can review in any order.</p>
                <p>In Order: Team members must review in the specified order. You can drag and drop to reorder team members below.</p>
                <select
                    id="team-review-member-order-setting"
                    value={teamReviewMemberOrderSetting}
                    onChange={handleTeamReviewSettingChange}
                    className="border rounded px-3 py-2"
                >
                    <option value={TeamReviewMemberOrderSetting.Any}>Any</option>
                    <option value={TeamReviewMemberOrderSetting.Sequential}>In Order</option>
                </select>
            </div>
            <div className="mt-6">
                <label className="block mb-2 font-semibold">Review Order</label>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                        items={teamReviewMemberOrder.map((member) => member.teamMemberId)}
                        strategy={verticalListSortingStrategy}
                    >
                        <ul className="bg-gray-100 rounded p-2">
                            {teamReviewMemberOrder.map((member) => (
                                <SortableItem key={member.teamMemberId} member={member} />
                            ))}
                        </ul>
                    </SortableContext>
                </DndContext>
            </div>
            <div className="mt-4">
                <button
                    className={`px-4 py-2 rounded ${settingsChanged ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-gray-400 text-gray-700 cursor-not-allowed"}`}
                    disabled={!settingsChanged}
                    onClick={handleSaveSettings}
                >
                    Save Changes
                </button>
            </div>
        </>
    );
}

const SortableItem: React.FC<{ member: TeamReviewMemberOrder }> = ({ member }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: member.teamMemberId,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="mb-2 p-3 bg-white rounded shadow flex items-center justify-between cursor-move"
        >
            <span>
                {member.firstName} {member.lastName}
            </span>
            <span className="text-xs text-gray-500">Position: {member.position + 1}</span>
        </li>
    );
};

export default TeamReviewSettingsComponent;